import React, { useState, useEffect } from 'react';

import { LinearProgress, styled } from '@mui/material';
import { linearProgressClasses } from '@mui/material/LinearProgress';
import BoltIcon from '@mui/icons-material/Bolt';

import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import '../stylesheet/homePage.css';

import { send_request } from '../../../utils/Request';
import { fetchUsageData } from '../../../utils/UsageAPI';
import FadeIn from 'react-fade-in';
import { Link } from 'react-router-dom';
import { DashboardCard, StyledSkeleton } from './CommonComponents';
import useProjectStore from '../../ProjectCanvas/useProjectStore';

/** Wait for InternalApp to finish billing initialization in useProjectStore */
const waitForBillingInit = () =>
  new Promise(resolve => {
    if (useProjectStore.getState().billingInitialized) return resolve();
    const interval = setInterval(() => {
      if (useProjectStore.getState().billingInitialized) {
        clearInterval(interval);
        resolve();
      }
    }, 50);
  });

const COLORS = {
  primary: 'rgb(33, 150, 243)',
  warning: '#FFA722',
  danger: 'rgba(176, 0, 32, 1)',
  unused: 'rgb(207, 216, 220)',
  text: 'var(--color-text-primary)',
  link: '#2a9af3',
};

const THRESHOLDS = {
  CRITICAL: 90,
  WARNING: 50,
};

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const BorderLinearProgress = styled(LinearProgress, {
  shouldForwardProp: prop => prop !== 'barColor',
})(({ barColor }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: barColor,
  },
}));

function ActionsUsedCard({ tier }) {
  const [usedActions, setUsedActions] = useState(null);
  const [remainingActions, setRemainingActions] = useState(null);
  const [renewalDate, setRenewalDate] = useState(null);
  const [percentage, setPercentage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isV2Billing, setIsV2Billing] = useState(null);

  useEffect(() => {
    const initializeCard = async () => {
      // Wait for InternalApp to finish initializing billing info in ProjectStore
      await waitForBillingInit();
      const billingVersion = useProjectStore.getState().isV2Billing ?? false;
      setIsV2Billing(billingVersion);

      if (billingVersion) {
        await fetchV2BillingData();
      } else {
        await fetchLegacyBillingData();
      }
    };

    initializeCard();
  }, []);

  const fetchV2BillingData = async () => {
    try {
      const [usageResponse, clientInfoResponse] = await Promise.all([
        fetchUsageData(),
        send_request('authz-service/organization/get-client-info', '', {}),
      ]);

      if (usageResponse.success && usageResponse.data) {
        const {
          usedActions: newUsedActions,
          totalActions,
          remainingActions: newRemainingActions,
        } = usageResponse.data;

        setUsedActions(newUsedActions === 0 ? 0.1 : newUsedActions);
        setRemainingActions(totalActions);

        const calculatedPercentage = calculateUsagePercentage(newUsedActions, totalActions);
        setPercentage(calculatedPercentage);

        const billingDate = clientInfoResponse?.data?.billingDate;
        const formattedRenewalDate = billingDate ? formatRenewalDate(billingDate) : null;

        setRenewalDate(formattedRenewalDate);
        setLoading(false);
      } else {
        console.error('Failed to fetch usage data:', usageResponse.error);
        await fetchLegacyBillingData();
      }
    } catch (error) {
      console.error('Error fetching V2 billing data:', error);
      await fetchLegacyBillingData();
    }
  };

  const fetchLegacyBillingData = async () => {
    try {
      const sessionData = await send_request(
        'authz-service/clientstats/get-client-session-data',
        '',
        '',
        'get'
      );

      const currentRun = sessionData.data.currentComponentSessionRun;
      const maxSession = sessionData.data.maxComponentSession;

      setUsedActions(currentRun === 0 ? 0.1 : currentRun);
      setRemainingActions(maxSession);

      const calculatedPercentage = calculateUsagePercentage(currentRun, maxSession);
      setPercentage(calculatedPercentage);

      const billingDate = sessionData.data.lastBillingDate;
      const formattedRenewalDate = formatRenewalDate(billingDate);

      setRenewalDate(formattedRenewalDate);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching legacy billing data:', error);
      setLoading(false);
    }
  };

  const calculateUsagePercentage = (used, total) => {
    if (total === 0) return 100;
    return Math.round((100 * used) / total);
  };

  // BANDAID FIX: Handle past billing dates by advancing them to future
  // TODO: Remove this once webhook issue is fixed on backend
  const formatRenewalDate = dateString => {
    if (!dateString) {
      return null;
    }

    let renewalDate;

    if (dateString && dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      renewalDate = new Date(year, month - 1, day);
    } else {
      renewalDate = new Date(dateString);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    renewalDate.setHours(0, 0, 0, 0);

    // BANDAID: If date is in the past, keep adding months until it's in the future
    let loopCount = 0;
    while (renewalDate < today) {
      loopCount++;
      renewalDate.setMonth(renewalDate.getMonth() + 1);

      // Safety check to prevent infinite loop
      if (loopCount > 12) {
        break;
      }
    }

    const formattedDate = `${renewalDate.getDate()} ${
      MONTH_NAMES[renewalDate.getMonth()]
    } ${renewalDate.getFullYear()}`;

    const timeDiff = renewalDate - today;
    const daysUntilRenewal = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    return { formattedDate, daysUntilRenewal };
  };

  const getProgressBarColor = percentage => {
    if (percentage >= THRESHOLDS.CRITICAL) return COLORS.danger;
    if (percentage >= THRESHOLDS.WARNING) return COLORS.warning;
    return COLORS.text;
  };

  const getProgressBar = (used, available) => {
    const usagePercentage = (used / available) * 100;
    const color = getProgressBarColor(usagePercentage);

    return (
      <div className="progress-bar-outer">
        <div className="text-inside-progress-bar" style={{ textAlign: 'left', width: '100%' }} />
        <BorderLinearProgress variant="determinate" value={usagePercentage} barColor={color} />
      </div>
    );
  };

  const actionWarningComponent = (
    <div className="actionWarningContainer">
      <FadeIn transitionDuration="1000">
        <div className="actionWarningContent">
          <p className="actionWarningText">
            You are about to run out of actions for this month. To prevent your workflows from being
            disrupted,{' '}
            <Link to="/organization" style={{ color: COLORS.link }}>
              upgrade your plan
            </Link>
          </p>
        </div>
      </FadeIn>
    </div>
  );

  const actionsUsedComponent = usedActions !== null && remainingActions !== null && (
    <DashboardCard className="cardStyleActions">
      <CardContent>
        <Typography
          variant="h4"
          component="p"
          className="cardHeader"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <BoltIcon sx={{ mr: 1 }} /> Actions used this month
        </Typography>
      </CardContent>

      {percentage >= THRESHOLDS.CRITICAL && actionWarningComponent}

      <CardContent className="cardTopContent">
        <FadeIn transitionDuration="1000">
          <div className="progress-bar-outer">{getProgressBar(usedActions, remainingActions)}</div>
          <Typography className="cardDescription">
            <strong style={{ color: COLORS.text }}>
              {Math.round(usedActions).toLocaleString()}
            </strong>{' '}
            actions used ({Math.round(remainingActions - usedActions).toLocaleString()} actions
            remaining out of{' '}
            <strong style={{ color: COLORS.text }}>{remainingActions.toLocaleString()}</strong>)
          </Typography>
          <Typography className="cardDescription">
            {tier !== 'FREE_TRIAL' && tier !== 'FREE' && renewalDate && (
              <>
                Renews in{' '}
                <strong style={{ color: COLORS.text }}>
                  {renewalDate.daysUntilRenewal}{' '}
                  {renewalDate.daysUntilRenewal === 1 ? 'day' : 'days'}
                </strong>{' '}
                on <strong style={{ color: COLORS.text }}>{renewalDate.formattedDate}</strong>
              </>
            )}
          </Typography>
        </FadeIn>
      </CardContent>
    </DashboardCard>
  );

  return loading ? (
    <DashboardCard>
      <CardContent>
        <Typography
          variant="h4"
          component="p"
          className="cardHeader"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <BoltIcon sx={{ mr: 1 }} /> Actions used this month
        </Typography>
      </CardContent>
      <CardContent className="cardTopContent">
        <StyledSkeleton variant="rectangular" height="10px" margin="16px 0" />
        <StyledSkeleton variant="rectangular" height="20px" width="80%" margin="8px 0" />
        <StyledSkeleton variant="rectangular" height="16px" width="60%" margin="8px 0" />
      </CardContent>
    </DashboardCard>
  ) : (
    actionsUsedComponent
  );
}

export default ActionsUsedCard;
